// Events list / dashboard. Lists upcoming events, lets you create
// new ones, edit, hide, delete. The events here feed the newsletter
// calendar block (auto-pull, 8-day forward window) and the future
// public events calendar on penobscotmorning.com.

import { useState } from 'react';
import { Plus, Calendar, Trash2, MoreHorizontal, Eye, EyeOff } from 'lucide-react';
import { useEventList } from '../hooks/useEventList';
import { eventsApi } from '../lib/api';
import { formatEventDate, todayIso } from '@shared/event-date';
import EventEditModal from './EventEditModal';
import type { Event, EventListItem } from '@shared/types';

export default function EventsListPage() {
  const [showHidden, setShowHidden] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const { events, loading, err, refresh } = useEventList({
    include_hidden: showHidden,
    include_past: showPast,
  });
  const [editing, setEditing] = useState<{ event: Event | null } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  async function openCreate() {
    setEditing({ event: null });
  }

  async function openEdit(id: string) {
    setOpening(id);
    try {
      const { event } = await eventsApi.get(id);
      setEditing({ event });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to load event');
    } finally {
      setOpening(null);
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl text-slate-900">Events</h1>
            <p className="text-sm text-slate-500 mt-1">
              Community events. The newsletter calendar block pulls the next 8 days from this list.
            </p>
          </div>
          <button onClick={openCreate} className="btn-primary self-start sm:self-auto">
            <Plus size={14} /> New event
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600 mb-4">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Show past events
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Show hidden
          </label>
        </div>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : events.length === 0 ? (
          <EmptyState onCreate={openCreate} />
        ) : (
          <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {events.map((ev) => (
              <EventRow
                key={ev.id}
                event={ev}
                onOpen={() => openEdit(ev.id)}
                onChange={refresh}
                opening={opening === ev.id}
              />
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <EventEditModal
          event={editing.event}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center">
      <Calendar size={28} className="mx-auto text-slate-300 mb-2" />
      <p className="text-sm font-medium text-slate-700">No upcoming events</p>
      <p className="text-xs text-slate-500 mt-1 mb-4">
        Add events here so the newsletter calendar block can pull them automatically.
      </p>
      <button onClick={onCreate} className="btn-primary py-2 px-4 mx-auto">
        <Plus size={14} /> Add your first event
      </button>
    </div>
  );
}

function EventRow({
  event,
  onOpen,
  onChange,
  opening,
}: {
  event: EventListItem;
  onOpen: () => void;
  onChange: () => Promise<void> | void;
  opening: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const dateLabel = formatEventDate(
    {
      startDate: event.startDate,
      startTime: event.startTime || undefined,
      endDate: event.endDate || undefined,
      endTime: event.endTime || undefined,
    },
    todayIso(),
  );

  async function run(action: string, fn: () => Promise<unknown>) {
    setBusy(action);
    try {
      await fn();
      await onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
      setMenuOpen(false);
    }
  }

  async function toggleHidden(e: React.MouseEvent) {
    e.stopPropagation();
    await run('hide', () =>
      eventsApi.update(event.id, { hidden: !event.hidden, expected_updated_at: event.updated_at }),
    );
  }

  async function del(e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Delete "${event.name}"? This can't be undone.`)) return;
    await run('delete', () => eventsApi.delete(event.id));
  }

  return (
    <li className="relative group">
      <button
        type="button"
        onClick={onOpen}
        disabled={opening}
        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 disabled:opacity-50"
      >
        <Calendar size={16} className="text-slate-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium truncate ${event.hidden ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
              {event.name}
            </span>
            {event.hidden && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                hidden
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            <span className="font-medium text-slate-600">{dateLabel}</span>
            {event.description && <> · {event.description}</>}
          </div>
        </div>
        <span
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((o) => !o); }}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
        >
          <MoreHorizontal size={14} />
        </span>
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-3 top-12 z-30 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-44">
            <button onClick={toggleHidden} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
              {event.hidden ? <><Eye size={12} /> Unhide</> : <><EyeOff size={12} /> Hide</>}
            </button>
            <div className="border-t border-slate-100 my-1" />
            <button onClick={del} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </>
      )}
      {busy && <span className="absolute right-12 top-3 text-xs text-slate-400">{busy}…</span>}
    </li>
  );
}
